import { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { EnvironmentSelect } from './EnvironmentSelect';
import { SetupSteps } from './SetupSteps';
import { ConfigPreview } from './ConfigPreview';
import {
  GitHubIcon,
  GitLabIcon,
  CircleCIIcon,
  VercelIcon,
  RailwayIcon,
  NodejsIcon,
  PythonIcon,
  TerminalIcon,
  KubernetesIcon,
  BitbucketIcon,
  NetlifyIcon,
  AWSLambdaIcon,
} from './icons';
import type { Integration } from './IntegrationCard';
import type { Environment } from '@/hooks/useProjects';

// Map integration IDs to their icon components
const INTEGRATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'github-actions': GitHubIcon,
  'gitlab-ci': GitLabIcon,
  'circleci': CircleCIIcon,
  'bitbucket-pipelines': BitbucketIcon,
  'vercel': VercelIcon,
  'netlify': NetlifyIcon,
  'railway': RailwayIcon,
  'node-sdk': NodejsIcon,
  'python-sdk': PythonIcon,
  'cli': TerminalIcon,
  'kubernetes-eso': KubernetesIcon,
  'aws-lambda': AWSLambdaIcon,
};

interface IntegrationDetailProps {
  integration: Integration;
  environments: Environment[];
  projectId?: string;
  onCreateToken: () => void;
}

// Configuration generators
function generateGitHubActionsConfig(envName: string, tokenVar: string, projectId?: string, scanSecrets?: boolean): string {
  const scanStep = scanSecrets ? `
      - name: Scan for secrets
        uses: keyenv/scan-action@v1
        with:
          severity: high
` : '';

  return `name: Deploy

on:
  push:
    branches: [main]
  pull_request:
    branches: [main]

jobs:
  deploy:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
${scanStep}
      - name: Load secrets from KeyEnv
        uses: keyenv/keyenv-action@v1
        with:
          token: \${{ secrets.${tokenVar} }}
          environment: ${envName}${projectId ? `
          project-id: ${projectId}` : ''}

      - name: Deploy
        run: npm run deploy`;
}

function generateGitLabCIConfig(envName: string, tokenVar: string, projectId?: string, scanSecrets?: boolean): string {
  const scanStage = scanSecrets ? `
  - security` : '';
  const scanJob = scanSecrets ? `

security-scan:
  stage: security
  image: ubuntu:latest
  before_script:
    - apt-get update && apt-get install -y curl
    - curl -fsSL https://keyenv.dev/install.sh | bash
    - export PATH="$HOME/.keyenv/bin:$PATH"
  script:
    - keyenv scan --severity high
  rules:
    - if: $CI_PIPELINE_SOURCE == "merge_request_event"
    - if: $CI_COMMIT_BRANCH == "main"
` : '';

  return `stages:${scanStage}
  - deploy
${scanJob}
deploy:
  stage: deploy
  image: node:20
  before_script:
    - npx keyenv pull --token $${tokenVar} --env ${envName}${projectId ? ` --project ${projectId}` : ''}
  script:
    - npm run deploy
  only:
    - main`;
}

function generateCircleCIConfig(envName: string, tokenVar: string, projectId?: string, scanSecrets?: boolean): string {
  const scanJob = scanSecrets ? `
  security-scan:
    docker:
      - image: cimg/base:current
    steps:
      - checkout
      - run:
          name: Install KeyEnv CLI
          command: curl -fsSL https://keyenv.dev/install.sh | bash
      - run:
          name: Scan for secrets
          command: ~/.keyenv/bin/keyenv scan --severity high
` : '';

  const scanWorkflowJob = scanSecrets ? `
      - security-scan` : '';

  return `version: 2.1

orbs:
  keyenv: keyenv/secrets@1.0

jobs:${scanJob}
  deploy:
    docker:
      - image: cimg/node:20.0
    steps:
      - checkout
      - keyenv/load:
          environment: ${envName}${projectId ? `
          project-id: ${projectId}` : ''}
          token-var: ${tokenVar}
      - run:
          name: Deploy
          command: npm run deploy

workflows:
  deploy:
    jobs:${scanWorkflowJob}
      - deploy:
          filters:
            branches:
              only: main`;
}

function generateVercelConfig(envName: string, tokenVar: string, projectId?: string): string {
  return `# In your Vercel project settings:
# 1. Go to Settings > Environment Variables
# 2. Add ${tokenVar} with your KeyEnv token

# In your build command or vercel.json:
{
  "buildCommand": "npx keyenv pull --env ${envName}${projectId ? ` --project ${projectId}` : ''} && npm run build",
  "env": {
    "${tokenVar}": "@keyenv-token"
  }
}`;
}

function generateRailwayConfig(envName: string, tokenVar: string, projectId?: string): string {
  return `#!/bin/bash
# Sync KeyEnv secrets to Railway
# Usage: KEYENV_TOKEN=xxx RAILWAY_TOKEN=xxx ./sync-to-railway.sh

set -euo pipefail

KEYENV_API="https://api.keyenv.dev"
KEYENV_PROJECT="${projectId || 'YOUR_PROJECT_ID'}"
KEYENV_ENV="${envName}"
RAILWAY_PROJECT="YOUR_RAILWAY_PROJECT_ID"
RAILWAY_ENV="YOUR_RAILWAY_ENVIRONMENT_ID"

# Fetch secrets from KeyEnv
secrets=$(curl -s -H "Authorization: Bearer $${tokenVar}" \\
  "$KEYENV_API/api/v1/projects/$KEYENV_PROJECT/environments/$KEYENV_ENV/secrets/export")

# Sync each secret to Railway via GraphQL
echo "$secrets" | jq -c '.secrets[]' | while read -r secret; do
  key=$(echo "$secret" | jq -r '.key')
  value=$(echo "$secret" | jq -r '.value')

  curl -s -X POST https://backboard.railway.app/graphql/v2 \\
    -H "Authorization: Bearer $RAILWAY_TOKEN" \\
    -H "Content-Type: application/json" \\
    -d "{
      \\"query\\": \\"mutation { variableUpsert(input: { projectId: \\\\\\"$RAILWAY_PROJECT\\\\\\", environmentId: \\\\\\"$RAILWAY_ENV\\\\\\", name: \\\\\\"$key\\\\\\", value: \\\\\\"$value\\\\\\" }) { id } }\\"
    }" > /dev/null

  echo "Synced: $key"
done

echo "Done! Railway will redeploy automatically."`;
}

function generateNodeSDKConfig(envName: string, tokenVar: string, projectId?: string): string {
  return `import { KeyEnv } from '@keyenv/sdk';

const keyenv = new KeyEnv({
  token: process.env.${tokenVar},
  environment: '${envName}',${projectId ? `
  projectId: '${projectId}',` : ''}
});

// Get all secrets
const secrets = await keyenv.getSecrets();

// Get a specific secret
const apiKey = await keyenv.get('API_KEY');

// Use with auto-reload
keyenv.watch((newSecrets) => {
  console.log('Secrets updated:', newSecrets);
});`;
}

function generatePythonSDKConfig(envName: string, tokenVar: string, projectId?: string): string {
  return `import os
from keyenv import KeyEnv

keyenv = KeyEnv(
    token=os.environ["${tokenVar}"],
    environment="${envName}",${projectId ? `
    project_id="${projectId}",` : ''}
)

# Get all secrets
secrets = keyenv.get_secrets()

# Get a specific secret
api_key = keyenv.get("API_KEY")

# Use as context manager
with keyenv.load():
    # Secrets are available as environment variables
    print(os.environ["API_KEY"])`;
}

function generateCLIConfig(envName: string, tokenVar: string, projectId?: string): string {
  const projectFlag = projectId ? ` --project ${projectId}` : '';
  return `# Install the CLI
npm install -g @keyenv/cli

# Authenticate
export ${tokenVar}=<your-token>

# Pull secrets to .env file
keyenv pull --env ${envName}${projectFlag}

# Run a command with secrets injected
keyenv run --env ${envName}${projectFlag} -- npm start

# Push local .env changes
keyenv push --env ${envName}${projectFlag}`;
}

function generateKubernetesESOConfig(envName: string, _tokenVar: string, projectId?: string): string {
  return `# Step 1: Create a Kubernetes Secret with your KeyEnv token
apiVersion: v1
kind: Secret
metadata:
  name: keyenv-token
  namespace: external-secrets
type: Opaque
stringData:
  token: "<your-keyenv-service-token>"

---
# Step 2: Create a ClusterSecretStore
apiVersion: external-secrets.io/v1beta1
kind: ClusterSecretStore
metadata:
  name: keyenv
spec:
  provider:
    webhook:
      url: "https://api.keyenv.dev/api/v1/eso/secret?project=${projectId || '<PROJECT_ID>'}&env={{ .remoteRef.property }}&key={{ .remoteRef.key }}"
      result:
        jsonPath: "$.value"
      headers:
        Authorization: "Bearer {{ .auth.token }}"
      secrets:
        - name: auth
          secretRef:
            namespace: external-secrets
            name: keyenv-token
            key: token

---
# Step 3: Create an ExternalSecret
apiVersion: external-secrets.io/v1beta1
kind: ExternalSecret
metadata:
  name: my-app-secrets
  namespace: default
spec:
  refreshInterval: 1h
  secretStoreRef:
    name: keyenv
    kind: ClusterSecretStore
  target:
    name: my-app-secrets
  data:
    - secretKey: DATABASE_URL
      remoteRef:
        key: DATABASE_URL
        property: ${envName}
    - secretKey: API_KEY
      remoteRef:
        key: API_KEY
        property: ${envName}`;
}

function generateBitbucketPipelinesConfig(envName: string, tokenVar: string, projectId?: string, scanSecrets?: boolean): string {
  const scanStep = scanSecrets ? `    - step:
        name: Security Scan
        script:
          - curl -fsSL https://keyenv.dev/install.sh | bash
          - export PATH="$HOME/.keyenv/bin:$PATH"
          - keyenv scan --severity high
` : '';

  return `# bitbucket-pipelines.yml
pipelines:
  default:
${scanStep}    - step:
        name: Build and Test
        script:
          - pipe: keyenv/keyenv-pipe:1.0.0
            variables:
              KEYENV_TOKEN: $${tokenVar}
              ENVIRONMENT: ${envName}${projectId ? `
              PROJECT_ID: ${projectId}` : ''}
          - npm ci
          - npm test

  branches:
    main:
${scanStep}      - step:
          name: Deploy to Production
          deployment: production
          script:
            - pipe: keyenv/keyenv-pipe:1.0.0
              variables:
                KEYENV_TOKEN: $${tokenVar}
                ENVIRONMENT: production${projectId ? `
                PROJECT_ID: ${projectId}` : ''}
            - npm run deploy`;
}

function generateNetlifyConfig(envName: string, tokenVar: string, projectId?: string): string {
  return `# netlify.toml
[build]
  command = "npm run build"
  publish = "dist"

[[plugins]]
  package = "@keyenv/netlify-plugin"

  [plugins.inputs]
    environment = "${envName}"${projectId ? `
    project_id = "${projectId}"` : ''}

# Optional: Custom context mapping
# [[plugins]]
#   package = "@keyenv/netlify-plugin"
#   [plugins.inputs.context_mapping]
#     production = "production"
#     deploy-preview = "staging"
#     branch-deploy = "development"

# Set ${tokenVar} in Netlify dashboard:
# Site settings > Environment variables > Add variable`;
}

function generateAWSLambdaConfig(envName: string, tokenVar: string, projectId?: string): string {
  return `# serverless.yml (Serverless Framework)
service: my-service

provider:
  name: aws
  runtime: nodejs20.x
  layers:
    - arn:aws:lambda:\${aws:region}:123456789012:layer:keyenv:1
  environment:
    ${tokenVar}: \${ssm:/keyenv/token}
    KEYENV_ENVIRONMENT: ${envName}${projectId ? `
    KEYENV_PROJECT_ID: ${projectId}` : ''}

functions:
  api:
    handler: handler.main
    events:
      - http:
          path: /
          method: get

# ---
# Alternative: AWS SAM (template.yaml)
# ---
# Globals:
#   Function:
#     Layers:
#       - arn:aws:lambda:!Ref AWS::Region:123456789012:layer:keyenv:1
#     Environment:
#       Variables:
#         ${tokenVar}: !Ref KeyEnvToken
#         KEYENV_ENVIRONMENT: ${envName}${projectId ? `
#         KEYENV_PROJECT_ID: ${projectId}` : ''}

# ---
# Alternative: Terraform
# ---
# resource "aws_lambda_function" "api" {
#   layers = ["arn:aws:lambda:\${var.region}:123456789012:layer:keyenv:1"]
#   environment {
#     variables = {
#       ${tokenVar}          = var.keyenv_token
#       KEYENV_ENVIRONMENT   = "${envName}"${projectId ? `
#       KEYENV_PROJECT_ID    = "${projectId}"` : ''}
#     }
#   }
# }`;
}

// CI/CD integrations that support secret scanning
const SCAN_SUPPORTED_INTEGRATIONS = ['github-actions', 'gitlab-ci', 'circleci', 'bitbucket-pipelines'];

function getConfigGenerator(integrationId: string): (envName: string, tokenVar: string, projectId?: string, scanSecrets?: boolean) => string {
  const generators: Record<string, (envName: string, tokenVar: string, projectId?: string, scanSecrets?: boolean) => string> = {
    'github-actions': generateGitHubActionsConfig,
    'gitlab-ci': generateGitLabCIConfig,
    'circleci': generateCircleCIConfig,
    'bitbucket-pipelines': generateBitbucketPipelinesConfig,
    'vercel': generateVercelConfig,
    'netlify': generateNetlifyConfig,
    'railway': generateRailwayConfig,
    'node-sdk': generateNodeSDKConfig,
    'python-sdk': generatePythonSDKConfig,
    'cli': generateCLIConfig,
    'kubernetes-eso': generateKubernetesESOConfig,
    'aws-lambda': generateAWSLambdaConfig,
  };
  return generators[integrationId] || (() => '# Configuration not available');
}

type SetupStep = {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick?: () => void;
    href?: string;
    external?: boolean;
  };
};

function getSetupSteps(integrationId: string, tokenVar: string, onCreateToken: () => void): SetupStep[] {
  const baseSteps: SetupStep[] = [
    {
      title: 'Create a service token',
      description: 'Generate a token with read access to your project secrets.',
      action: {
        label: 'Create Token',
        onClick: onCreateToken,
      },
    },
  ];

  const integrationSteps: Record<string, SetupStep[]> = {
    'github-actions': [
      ...baseSteps,
      {
        title: 'Add token to GitHub Secrets',
        description: `Go to your repository Settings > Secrets and add ${tokenVar}.`,
        action: {
          label: 'GitHub Docs',
          href: 'https://docs.github.com/en/actions/security-for-github-actions/security-guides/encrypted-secrets',
          external: true,
        },
      },
      {
        title: 'Add the workflow',
        description: 'Copy the configuration below to your .github/workflows directory.',
      },
    ],
    'gitlab-ci': [
      ...baseSteps,
      {
        title: 'Add token to GitLab CI/CD Variables',
        description: `Go to Settings > CI/CD > Variables and add ${tokenVar}.`,
        action: {
          label: 'GitLab Docs',
          href: 'https://docs.gitlab.com/ee/ci/variables/',
          external: true,
        },
      },
      {
        title: 'Update .gitlab-ci.yml',
        description: 'Copy the configuration below to your pipeline file.',
      },
    ],
    'circleci': [
      ...baseSteps,
      {
        title: 'Add token to CircleCI Environment Variables',
        description: `Go to Project Settings > Environment Variables and add ${tokenVar}.`,
        action: {
          label: 'CircleCI Docs',
          href: 'https://circleci.com/docs/set-environment-variable/',
          external: true,
        },
      },
      {
        title: 'Update config.yml',
        description: 'Copy the configuration below to your .circleci directory.',
      },
    ],
    'vercel': [
      ...baseSteps,
      {
        title: 'Add token to Vercel Environment Variables',
        description: `Go to Project Settings > Environment Variables and add ${tokenVar}.`,
        action: {
          label: 'Vercel Docs',
          href: 'https://vercel.com/docs/projects/environment-variables',
          external: true,
        },
      },
      {
        title: 'Configure build command',
        description: 'Update your build settings to pull secrets before building.',
      },
    ],
    'railway': [
      ...baseSteps,
      {
        title: 'Get Railway API token',
        description: 'Create a Railway API token at railway.app/account/tokens.',
        action: {
          label: 'Railway Tokens',
          href: 'https://railway.app/account/tokens',
          external: true,
        },
      },
      {
        title: 'Find Railway Project and Environment IDs',
        description: 'Get these from your Railway project URL or GraphQL API.',
      },
      {
        title: 'Run the sync script',
        description: `Set ${tokenVar} and RAILWAY_TOKEN, then run the script.`,
      },
    ],
    'node-sdk': [
      {
        title: 'Install the SDK',
        description: 'Add @keyenv/sdk to your project dependencies.',
        action: {
          label: 'npm install @keyenv/sdk',
          href: 'https://www.npmjs.com/package/@keyenv/sdk',
          external: true,
        },
      },
      ...baseSteps,
      {
        title: 'Initialize and use',
        description: 'Use the code example below to load secrets in your application.',
      },
    ],
    'python-sdk': [
      {
        title: 'Install the SDK',
        description: 'Add keyenv to your project dependencies.',
        action: {
          label: 'pip install keyenv',
          href: 'https://pypi.org/project/keyenv/',
          external: true,
        },
      },
      ...baseSteps,
      {
        title: 'Initialize and use',
        description: 'Use the code example below to load secrets in your application.',
      },
    ],
    'cli': [
      {
        title: 'Install the CLI',
        description: 'Install @keyenv/cli globally or as a dev dependency.',
        action: {
          label: 'npm install -g @keyenv/cli',
          href: 'https://www.npmjs.com/package/@keyenv/cli',
          external: true,
        },
      },
      ...baseSteps,
      {
        title: 'Run commands',
        description: 'Use keyenv pull, push, or run to manage your secrets.',
      },
    ],
    'kubernetes-eso': [
      {
        title: 'Install External Secrets Operator',
        description: 'Install ESO in your Kubernetes cluster using Helm or kubectl.',
        action: {
          label: 'ESO Installation Guide',
          href: 'https://external-secrets.io/latest/introduction/getting-started/',
          external: true,
        },
      },
      ...baseSteps,
      {
        title: 'Create K8s Secret with token',
        description: 'Store your KeyEnv service token as a Kubernetes Secret.',
      },
      {
        title: 'Create ClusterSecretStore',
        description: 'Configure ESO to connect to KeyEnv using the webhook provider.',
      },
      {
        title: 'Create ExternalSecret',
        description: 'Define which secrets to sync from KeyEnv to your cluster.',
      },
    ],
    'bitbucket-pipelines': [
      ...baseSteps,
      {
        title: 'Add token to Repository Variables',
        description: `Go to Repository settings > Pipelines > Repository variables and add ${tokenVar}.`,
        action: {
          label: 'Bitbucket Docs',
          href: 'https://support.atlassian.com/bitbucket-cloud/docs/variables-and-secrets/',
          external: true,
        },
      },
      {
        title: 'Update bitbucket-pipelines.yml',
        description: 'Copy the configuration below to your pipeline file.',
      },
    ],
    'netlify': [
      {
        title: 'Install the build plugin',
        description: 'Add @keyenv/netlify-plugin to your project.',
        action: {
          label: 'npm install @keyenv/netlify-plugin',
          href: 'https://www.npmjs.com/package/@keyenv/netlify-plugin',
          external: true,
        },
      },
      ...baseSteps,
      {
        title: 'Add token to Netlify Environment Variables',
        description: `Go to Site settings > Environment variables and add ${tokenVar}.`,
        action: {
          label: 'Netlify Docs',
          href: 'https://docs.netlify.com/environment-variables/overview/',
          external: true,
        },
      },
      {
        title: 'Configure netlify.toml',
        description: 'Add the plugin configuration to your netlify.toml file.',
      },
    ],
    'aws-lambda': [
      ...baseSteps,
      {
        title: 'Store token in AWS SSM',
        description: `Store your KeyEnv token in AWS Systems Manager Parameter Store as /keyenv/token.`,
        action: {
          label: 'AWS SSM Docs',
          href: 'https://docs.aws.amazon.com/systems-manager/latest/userguide/systems-manager-parameter-store.html',
          external: true,
        },
      },
      {
        title: 'Add the Lambda Layer',
        description: 'Add the KeyEnv layer ARN to your Lambda function configuration.',
      },
      {
        title: 'Set environment variables',
        description: 'Configure KEYENV_TOKEN and KEYENV_ENVIRONMENT in your function.',
      },
    ],
  };

  return integrationSteps[integrationId] || baseSteps;
}

type SupportedLanguage = 'yaml' | 'json' | 'typescript' | 'python' | 'bash' | 'shell';

function getConfigLanguage(integrationId: string): SupportedLanguage {
  const languages: Record<string, SupportedLanguage> = {
    'github-actions': 'yaml',
    'gitlab-ci': 'yaml',
    'circleci': 'yaml',
    'bitbucket-pipelines': 'yaml',
    'vercel': 'bash',
    'netlify': 'bash',
    'railway': 'bash',
    'node-sdk': 'typescript',
    'python-sdk': 'python',
    'cli': 'bash',
    'kubernetes-eso': 'yaml',
    'aws-lambda': 'yaml',
  };
  return languages[integrationId] || 'bash';
}

export function IntegrationDetail({
  integration,
  environments,
  projectId,
  onCreateToken,
}: IntegrationDetailProps) {
  const [selectedEnvId, setSelectedEnvId] = useState<string | null>(
    environments[0]?.id || null
  );
  const [tokenVar, setTokenVar] = useState('KEYENV_TOKEN');
  const [scanSecrets, setScanSecrets] = useState(true);

  const selectedEnv = environments.find((e) => e.id === selectedEnvId);
  const envName = selectedEnv?.name || 'production';
  const showScanOption = SCAN_SUPPORTED_INTEGRATIONS.includes(integration.id);

  const config = useMemo(() => {
    const generator = getConfigGenerator(integration.id);
    return generator(envName, tokenVar, projectId, showScanOption ? scanSecrets : undefined);
  }, [integration.id, envName, tokenVar, projectId, scanSecrets, showScanOption]);

  const steps = useMemo(() => {
    return getSetupSteps(integration.id, tokenVar, onCreateToken);
  }, [integration.id, tokenVar, onCreateToken]);

  const language = getConfigLanguage(integration.id);
  const IconComponent = INTEGRATION_ICONS[integration.id] || TerminalIcon;

  return (
    <motion.div
      key={integration.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
            <IconComponent className="h-12 w-12" />
          
          <div>
            <h2 className="text-xl font-semibold text-foreground">{integration.name}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{integration.description}</p>
          </div>
        </div>
        {integration.docsUrl && (
          <Button variant="outline" size="sm" asChild className="gap-1.5 flex-shrink-0">
            <a href={integration.docsUrl} target="_blank" rel="noopener noreferrer">
              Docs
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        )}
      </div>

      {/* Configuration Options */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label htmlFor="environment" className="text-sm font-medium">
            Environment
          </Label>
          <EnvironmentSelect
            environments={environments}
            value={selectedEnvId}
            onChange={setSelectedEnvId}
            className="mt-1.5"
            disabled={environments.length === 0}
          />
        </div>
        <div>
          <Label htmlFor="tokenVar" className="text-sm font-medium">
            Token Variable Name
          </Label>
          <Input
            id="tokenVar"
            value={tokenVar}
            onChange={(e) => setTokenVar(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            placeholder="KEYENV_TOKEN"
            className="mt-1.5 bg-muted/30 border-transparent focus:border-primary/50 font-mono text-sm"
          />
        </div>
      </div>

      {/* Scan Secrets Option - Only for CI/CD integrations */}
      {showScanOption ? (
        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/20 p-3 mb-6">
          <div className="space-y-0.5">
            <Label htmlFor="scanSecrets" className="text-sm font-medium cursor-pointer">
              Scan for hardcoded secrets
            </Label>
            <p className="text-xs text-muted-foreground">
              Add a security scan step to detect leaked secrets before deployment
            </p>
          </div>
          <Switch
            id="scanSecrets"
            checked={scanSecrets}
            onCheckedChange={setScanSecrets}
          />
        </div>
      ) : null}

      {/* Setup Steps */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Setup Steps</h3>
        <SetupSteps steps={steps} />
      </div>

      {/* Config Preview */}
      <div className="flex-1 min-h-0">
        <h3 className="text-sm font-semibold text-foreground mb-3">Configuration</h3>
        <ConfigPreview
          code={config}
          language={language}
          className="h-full"
        />
      </div>
    </motion.div>
  );
}
